import {
  api,
  normalizePrivateProfile,
  parseCategory,
  parseList,
} from '@/lib/api'

import type {
  ActivityDetail,
  Category,
  CitySuggestion,
  CoverUploadResponse,
  CreateActivityRequest,
  CreateActivityResponse,
  PlaceSuggestion,
  PrivateProfile,
  UpdateActivityRequest,
} from './types'

interface RequestOptions {
  signal?: AbortSignal
}

const publicRequest = { skipAuth: true, skipRefresh: true } as const

/**
 * Thin feature boundary over the frozen activity-composer endpoints. The
 * backend owns server state (D-042); screens call these methods directly and
 * keep their own loading/error/retry lifecycle.
 *
 * Location search deliberately goes through the API only: the browser never
 * calls Nominatim or Photon.
 */
export const activityComposerApi = {
  listCategories(options: RequestOptions = {}): Promise<Category[]> {
    return api
      .get<Category[]>('/categories', {
        ...publicRequest,
        signal: options.signal,
      })
      .then((items) => parseList(items, '/categories', parseCategory))
  },

  getProfile(options: RequestOptions = {}): Promise<PrivateProfile> {
    return api
      .get<PrivateProfile>('/me', { signal: options.signal })
      .then(normalizePrivateProfile)
  },

  getActivity(
    activityId: string,
    options: RequestOptions = {},
  ): Promise<ActivityDetail> {
    return api.get<ActivityDetail>(
      `/activities/${encodeURIComponent(activityId)}`,
      { signal: options.signal },
    )
  },

  createActivity(body: CreateActivityRequest): Promise<CreateActivityResponse> {
    return api.post<CreateActivityResponse>('/activities', body)
  },

  updateActivity(
    activityId: string,
    body: UpdateActivityRequest,
  ): Promise<ActivityDetail> {
    return api.patch<ActivityDetail>(
      `/activities/${encodeURIComponent(activityId)}`,
      body,
    )
  },

  uploadCover(
    activityId: string,
    file: File,
  ): Promise<CoverUploadResponse> {
    const body = new FormData()
    body.append('file', file)
    return api.post<CoverUploadResponse>(
      `/activities/${encodeURIComponent(activityId)}/cover`,
      body,
    )
  },

  cancelActivity(activityId: string): Promise<{ ok: true }> {
    return api.delete<{ ok: true }>(
      `/activities/${encodeURIComponent(activityId)}`,
    )
  },

  suggestCities(
    query: string,
    options: RequestOptions = {},
  ): Promise<{ items: CitySuggestion[] }> {
    return api.post<{ items: CitySuggestion[] }>(
      '/location/suggestions',
      { query },
      { ...publicRequest, signal: options.signal },
    )
  },

  suggestPlaces(
    query: string,
    city: string,
    latitude: number,
    longitude: number,
    options: RequestOptions = {},
  ): Promise<{ items: PlaceSuggestion[] }> {
    return api.post<{ items: PlaceSuggestion[] }>(
      '/location/places',
      { query, city, latitude, longitude },
      { ...publicRequest, signal: options.signal },
    )
  },
}
